import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell, FaCalendarAlt, FaBars } from "react-icons/fa";
import { MdLanguage } from "react-icons/md";
import axios from 'axios';
import "../AdminPanel.css";

const AdminNavbar = () => {
  const navigate = useNavigate();
  const [userFullName, setUserFullName] = useState("");
  const [userImage, setUserImage] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  // ==================== REFS FOR OUTSIDE CLICK DETECTION ====================
  const notificationRef = useRef(null);
  const languageRef = useRef(null);
  // ==========================================================================

  const getDefaultAvatar = (name) => {
    if (!name) return "https://ui-avatars.com/api/?name=User&background=F6CA13&color=2B363C&size=35";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=F6CA13&color=2B363C&size=35`;
  };

  // ==================== HANDLE CLICK OUTSIDE TO CLOSE DROPDOWNS ====================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // ==============================================================================

  useEffect(() => {
    const storedFName = localStorage.getItem("fname");
    const storedLName = localStorage.getItem("lname");
    const storedUserName = localStorage.getItem("userName");
    const storedUserId = localStorage.getItem("userId") || localStorage.getItem("user_id");

    let fullName = "User";
    if (storedFName && storedLName) {
      fullName = `${storedFName} ${storedLName}`;
    } else if (storedFName) {
      fullName = storedFName;
    } else if (storedUserName) {
      fullName = storedUserName;
    }
    setUserFullName(fullName);
    setUserImage(getDefaultAvatar(fullName));

    if (storedUserId) {
      fetchUserProfile(storedUserId, fullName);
      fetchNotifications(); // Fetch on page load
      
      // ==========================================
      // NEW: TRACK USER ACTIVITY & PING
      // ==========================================
      trackUserActivity(storedUserId);
      const activityInterval = setInterval(() => trackUserActivity(storedUserId), 30000); // Every 30 seconds
      
      const handleVisibility = () => {
        if (!document.hidden) {
          trackUserActivity(storedUserId);
        }
      };
      document.addEventListener("visibilitychange", handleVisibility);
      
      return () => {
        clearInterval(activityInterval);
        document.removeEventListener("visibilitychange", handleVisibility);
      };
      // ==========================================
    } else {
      setLoading(false);
    }

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

    // ===== TRACK USER ACTIVITY (Global) =====
  const trackUserActivity = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post('https://skcapi.btcjapan.co/track_user_activity.php', 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Activity tracking error:", error);
    }
  };

  const fetchUserProfile = async (userId, fullName) => {
    try {
      const response = await fetch("https://skcapi.btcjapan.co/get_user_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: userId }),
      });
      const data = await response.json();
      if (data.Status === "200" && data.User) {
        if (data.User.fname && data.User.lname) {
          const name = `${data.User.fname} ${data.User.lname}`;
          setUserFullName(name);
          localStorage.setItem("fname", data.User.fname);
          localStorage.setItem("lname", data.User.lname);
        }
        if (data.User.image_url) {
          setUserImage(data.User.image_url);
        } else {
          setUserImage(getDefaultAvatar(fullName));
        }
      } else {
        setUserImage(getDefaultAvatar(fullName));
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserImage(getDefaultAvatar(fullName));
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("id") || localStorage.getItem("user_id") || localStorage.getItem("userId") || localStorage.getItem("userID");

      if (!userId) return;

      const res = await axios.get(`https://skcapi.btcjapan.co/get_notifications.php?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        setNotifications(res.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const token = localStorage.getItem("token");

      await axios.post(
        'https://skcapi.btcjapan.co/mark_as_read.php',
        { id: notification.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update UI immediately
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id ? { ...n, is_read: 1 } : n
        )
      );

      setShowNotifications(false);

      // Navigate
      if (notification.type === 'Bid') {
        navigate('/view-bids');
      } else if (notification.type === 'Lead') {
        navigate('/view-leads');
      } else if (notification.type === 'Client') {
        navigate('/view-client');
      }
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId =
        localStorage.getItem("id") ||
        localStorage.getItem("user_id") ||
        localStorage.getItem("userId") ||
        localStorage.getItem("userID");

      if (!userId) return;

      await axios.post(
        'https://skcapi.btcjapan.co/mark_all_as_read.php',
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update UI immediately
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      
      // Optional: refresh from server
      // fetchNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const updateTime = () => {
    const now = new Date();
    const options = { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    };
    setCurrentTime(now.toLocaleDateString('en-US', options));
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("userName");
    localStorage.removeItem("usertype");
    localStorage.removeItem("fname");
    localStorage.removeItem("lname");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userId");
    localStorage.removeItem("userImage");
    navigate("/sign-in");
  };

  const unreadCount = notifications.filter(n => n.is_read == 0 || n.is_read === false).length;

  const toggleNotifications = () => { 
    setShowNotifications(!showNotifications); 
    if (!showNotifications) {
      setShowLanguageMenu(false);
      fetchNotifications();
    }
  };

  const toggleLanguageMenu = () => { setShowLanguageMenu(!showLanguageMenu); if (!showLanguageMenu) setShowNotifications(false); };
  const handleLanguageSelect = (lang) => { setSelectedLanguage(lang); setShowLanguageMenu(false); };

  const toggleSidebar = () => {
    const adminContainer = document.querySelector('.admin-container');
    if (adminContainer) {
      adminContainer.classList.toggle('sidebar-collapsed');
    }
  };

  return (
    <div className="admin-navbar">
      <div className="navbar-left">
        <button className="sidebar-toggle-btn" onClick={toggleSidebar} title="Toggle Sidebar">
          <FaBars />
        </button>        
      </div>
      
      <div className="navbar-right">
        <div className="language-selector" ref={languageRef} onClick={toggleLanguageMenu}>
          <MdLanguage className="nav-icon" />
          <span className="language-text">{selectedLanguage}</span>
          <div className={`language-dropdown-menu ${showLanguageMenu ? 'active' : ''}`}>
            <div className="language-dropdown-item" onClick={() => handleLanguageSelect("English")}>English</div>
            <div className="language-dropdown-item" onClick={() => handleLanguageSelect("日本語")}>日本語</div>
            <div className="language-dropdown-item" onClick={() => handleLanguageSelect("العربية")}>العربية</div>
          </div>
        </div>
        <div className="calendar-icon"><FaCalendarAlt className="nav-icon" /></div>
        
        <div className="notification-wrapper" ref={notificationRef}>
          <div className="notification-icon" onClick={toggleNotifications}>
            <FaBell className="nav-icon" />
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </div>
          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <button 
                    onClick={markAllAsRead} 
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#007bff', 
                      fontWeight: 600, 
                      fontSize: '12px',
                      cursor: 'pointer',
                      padding: '0'
                    }}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div style={{ padding: '15px', textAlign: 'center', color: '#999' }}>No notifications found.</div>
                ) : (
                  notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`notification-item ${notification.is_read == 0 ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="notification-content">
                        <span className="notification-user">{notification.type}</span>
                        <span className="notification-message" style={{ fontWeight: notification.is_read == 0 ? 600 : 400 }}>
                          {notification.title}
                        </span>
                        <span className="notification-message">{notification.message}</span>
                        <span className="notification-time">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                      {notification.is_read == 0 && <div className="notification-dot"></div>}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="user-profile">
          {!loading && (
            <img src={userImage || getDefaultAvatar(userFullName || "User")} alt={userFullName || "User"} className="user-avatar"
              onError={(e) => { e.target.onerror = null; e.target.src = getDefaultAvatar(userFullName || "User"); }} />
          )}
          <span className="user-name-display">{userFullName || "User"}</span>
        </div>
        <button onClick={logout} className="logout-btn btn btn-warning">Logout</button>
      </div>
    </div>
  );
};

export default AdminNavbar;